import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useCart, useSharedBookLink } from '../context/CartContext';
import { Button } from '../components/common/Button';
import { COLORS } from '../constants/colors';
import { useTranslation } from 'react-i18next';
import { getOptimizedImage } from '../utils/cloudinary';
import { trackAddToCart, trackViewBook } from '../utils/metaPixel';

const CartContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 24px;
`;

const PageTitle = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.5rem;
  color: ${COLORS.PRIMARY};
  margin: 0 0 32px 0;
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 64px 24px;
  
  svg {
    width: 80px;
    height: 80px;
    color: #dee2e6;
    margin-bottom: 16px;
  }
  
  h2 {
    font-size: 1.5rem;
    color: #495057;
    margin: 0 0 16px 0;
  }
  
  p {
    color: #6c757d;
    margin: 0 0 24px 0;
  }
`;

const CartItems = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CartItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const ItemImage = styled.div`
  width: 100px;
  height: 133px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ItemTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
  margin: 0 0 8px 0;
`;

const ItemPrice = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  color: #495057;
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 4px;
`;

const QuantityButton = styled.button`
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #e9ecef;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Quantity = styled.span`
  font-weight: 500;
  min-width: 30px;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 4px 8px;
  transition: all 0.2s;
  
  &:hover {
    color: #c82333;
    text-decoration: underline;
  }
`;

const CartSummary = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  font-size: 1.1rem;
  
  &.total {
    font-size: 1.5rem;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
    padding-top: 16px;
    border-top: 2px solid #e9ecef;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  
  @media (max-width: 576px) {
    flex-direction: column-reverse;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 0.938rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  font-size: 0.938rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  font-size: 0.938rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 4px;
  display: block;
`;

const InputWithError = styled(Input)<{ $hasError?: boolean }>`
  border-color: ${props => props.$hasError ? '#dc3545' : '#dee2e6'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#dc3545' : COLORS.PRIMARY};
  }
`;

const DeliveryCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

// ============================================
// New: Loading & Error states for shared links
// ============================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e9ecef;
  border-top-color: ${COLORS.PRIMARY};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin-bottom: 16px;
`;

const SharedBookError = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
  color: #856404;
  font-size: 0.95rem;
`;

// ============================================
// Component
// ============================================

export const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { items, updateQuantity, removeFromCart, getTotal, clearCart, getCartCurrency, getAllowedCountries } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Handle shared book link (?bookId=...)
  const { isLoading: isLoadingSharedBook, error: sharedBookError } = useSharedBookLink();

  // Delivery form state
  const [deliveryDetails, setDeliveryDetails] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Romania'
  });

  const [emailError, setEmailError] = useState<string>('');

  // Track Meta Pixel: view book
  React.useEffect(() => {
    if (items.length > 0) {
      items.forEach(item => {
        trackViewBook(item.title);
      });
    }
  }, [items.length]);

  const SHIPPING_COSTS: Record<string, number> = {
    Romania: 4.0,
    Belgium: 15.0,
    Bulgaria: 15.0,
    Estonia: 17.0,
    Denmark: 15.0,
    Finland: 18.0,
    France: 16.0,
    Greece: 15.0,
    Ireland: 21.0,
    Italy: 17.0,
    Croatia: 14.0,
    Latvia: 16.0,
    Lithuania: 15.0,
    Luxembourg: 15.0,
    Netherlands: 14.0,
    Austria: 14.0,
    Poland: 14.0,
    Portugal: 15.0,
    Slovakia: 15.0,
    Slovenia: 15.0,
    Spain: 17.0,
    Sweden: 19.0,
    Czech_Republic: 14.0,
    Germany: 5.0,
    Hungary: 14.0,
  };

  const cartCurrency = getCartCurrency();
  const allowedCountries = getAllowedCountries();
  
  // If cart has country-restricted items, shipping is free (included in price)
  const isFreeShipping = allowedCountries !== null;
  
  const currencySymbol = cartCurrency === 'RON' ? 'RON ' : '€';

  // Filter available countries based on cart restrictions
  const availableCountries = allowedCountries 
    ? Object.keys(SHIPPING_COSTS).filter(c => allowedCountries.includes(c))
    : Object.keys(SHIPPING_COSTS);

  // Auto-select country if only one is available
  const allowedCountriesKey = allowedCountries?.join(',') ?? '';

  useEffect(() => {
    if (allowedCountries && allowedCountries.length === 1) {
      setDeliveryDetails(prev => ({ ...prev, country: allowedCountries[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedCountriesKey]);

  const shippingCost = isFreeShipping ? 0 : (SHIPPING_COSTS[deliveryDetails.country] || 0);
  const subtotal = getTotal();
  const total = subtotal + shippingCost;

  const handleDeliveryChange = (field: string, value: string) => {
    setDeliveryDetails(prev => ({ ...prev, [field]: value }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (value: string) => {
    handleDeliveryChange('email', value);
    if (emailError) {
      setEmailError('');
    }
  };

  const handleEmailBlur = () => {
    if (deliveryDetails.email && !validateEmail(deliveryDetails.email)) {
      setEmailError(t('cart.delivery.emailError'));
    } else {
      setEmailError('');
    }
  };

  const isFormValid = () => {
    return (
      deliveryDetails.name.trim() !== '' &&
      deliveryDetails.email.trim() !== '' &&
      validateEmail(deliveryDetails.email) &&
      deliveryDetails.address.trim() !== '' &&
      deliveryDetails.city.trim() !== '' &&
      deliveryDetails.postalCode.trim() !== '' &&
      deliveryDetails.country.trim() !== ''
    );
  };

  const handleCheckout = async () => {
    if (!isFormValid()) {
      alert(t('cart.delivery.fillAllFields'));
      return;
    }

    setIsCheckingOut(true);

    items.forEach(item => {
      trackAddToCart(item.title, item.price * item.quantity);
    });

    try {
      const orderData = {
        items: items.map(item => ({
          bookId: item.bookId,
          quantity: item.quantity,
        })),
        deliveryDetails: {
          name: deliveryDetails.name,
          email: deliveryDetails.email,
          phone: deliveryDetails.phone,
          address: deliveryDetails.address,
          city: deliveryDetails.city,
          postalCode: deliveryDetails.postalCode,
          country: deliveryDetails.country,
        },
        shippingCost,
        subtotal,
        total,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/books/cart-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(orderData),
        }
      );

      const result = await response.json();

      if (result.success && result.data.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.data.checkoutUrl;
      } else {
        alert(result.error || 'Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      alert('An error occurred while processing your order. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  // Show loading while fetching shared book
  if (isLoadingSharedBook) {
    return (
      <CartContainer>
        <PageTitle>{t('cart.title')}</PageTitle>
        <LoadingOverlay>
          <Spinner />
          <p>{t('cart.loadingBook', 'Loading book...')}</p>
        </LoadingOverlay>
      </CartContainer>
    );
  }

  if (items.length === 0) {
    return (
      <CartContainer>
        <PageTitle>{t('cart.title')}</PageTitle>
        {sharedBookError && (
          <SharedBookError>{sharedBookError}</SharedBookError>
        )}
        <EmptyCart>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1"/>
            <circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          <h2>{t('cart.empty.title')}</h2>
          <p>{t('cart.empty.description')}</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            {t('cart.empty.browseBooks')}
          </Button>
        </EmptyCart>
      </CartContainer>
    );
  }

  return (
    <CartContainer>
      <PageTitle>{t('cart.title')}</PageTitle>

      {sharedBookError && (
        <SharedBookError>{sharedBookError}</SharedBookError>
      )}

      <CartItems>
        {items.map((item) => (
          <CartItem key={item.bookId}>
            <ItemImage>
              <img
                src={getOptimizedImage(item.imageUrl, 200)}
                alt={item.title}
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/100x133?text=Book';
                }}
              />
            </ItemImage>

            <ItemDetails>
              <div>
                <ItemTitle>{item.title}</ItemTitle>
                <ItemPrice>
                  {currencySymbol}{item.price.toFixed(2)} {t('cart.each')}
                </ItemPrice>
              </div>

              <ItemActions>
                <QuantityControl>
                  <QuantityButton
                    onClick={() => updateQuantity(item.bookId, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                    aria-label={t('cart.decreaseQuantity')}
                  >
                    −
                  </QuantityButton>
                  <Quantity>{item.quantity}</Quantity>
                  <QuantityButton
                    onClick={() => updateQuantity(item.bookId, item.quantity + 1)}
                    aria-label={t('cart.increaseQuantity')}
                  >
                    +
                  </QuantityButton>
                </QuantityControl>

                <RemoveButton onClick={() => removeFromCart(item.bookId)}>
                  {t('cart.remove')}
                </RemoveButton>
              </ItemActions>
            </ItemDetails>

            <ItemPrice style={{ fontWeight: 600, fontSize: '1.25rem' }}>
              {currencySymbol}{(item.price * item.quantity).toFixed(2)}
            </ItemPrice>
          </CartItem>
        ))}
      </CartItems>

      <DeliveryCard>
        <SectionTitle>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="1" y="3" width="15" height="13" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 8h5l3 3v5h-2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="5.5" cy="18.5" r="2.5"/>
            <circle cx="18.5" cy="18.5" r="2.5"/>
          </svg>
          {t('cart.delivery.title')}
        </SectionTitle>

        <FormGroup>
          <Label>{t('cart.delivery.fullName')}</Label>
          <Input
            type="text"
            value={deliveryDetails.name}
            onChange={(e) => handleDeliveryChange('name', e.target.value)}
            placeholder={t('cart.delivery.fullNamePlaceholder')}
            required
          />
        </FormGroup>

        <FormRow>
          <FormGroup>
            <Label>{t('cart.delivery.email')}</Label>
            <InputWithError
              type="email"
              value={deliveryDetails.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              placeholder={t('cart.delivery.emailPlaceholder')}
              $hasError={!!emailError}
              required
            />
            {emailError && <ErrorMessage>{emailError}</ErrorMessage>}
          </FormGroup>
          <FormGroup>
            <Label>{t('cart.delivery.phone')}</Label>
            <Input
              type="tel"
              value={deliveryDetails.phone}
              onChange={(e) => handleDeliveryChange('phone', e.target.value)}
              placeholder={t('cart.delivery.phonePlaceholder')}
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label>{t('cart.delivery.address')}</Label>
          <TextArea
            value={deliveryDetails.address}
            onChange={(e) => handleDeliveryChange('address', e.target.value)}
            placeholder={t('cart.delivery.addressPlaceholder')}
            required
          />
        </FormGroup>

        <FormRow>
          <FormGroup>
            <Label>{t('cart.delivery.city')}</Label>
            <Input
              type="text"
              value={deliveryDetails.city}
              onChange={(e) => handleDeliveryChange('city', e.target.value)}
              placeholder={t('cart.delivery.cityPlaceholder')}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>{t('cart.delivery.postalCode')}</Label>
            <Input
              type="text"
              value={deliveryDetails.postalCode}
              onChange={(e) => handleDeliveryChange('postalCode', e.target.value)}
              placeholder={t('cart.delivery.postalCodePlaceholder')}
              required
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label>{t('cart.delivery.country')}</Label>
          <Select
            value={deliveryDetails.country}
            onChange={(e) => handleDeliveryChange('country', e.target.value)}
            disabled={allowedCountries !== null && allowedCountries.length === 1}
          >
            {availableCountries.sort().map(country => (
              <option key={country} value={country}>
                {country.replace(/_/g, ' ')}
              </option>
            ))}
          </Select>
        </FormGroup>
      </DeliveryCard>

      <CartSummary>
        <SummaryRow>
          <span>{t('cart.subtotal')}:</span>
          <span>{currencySymbol}{subtotal.toFixed(2)}</span>
        </SummaryRow>
        <SummaryRow>
          <span>{t('cart.shipping')} ({deliveryDetails.country.replace(/_/g, ' ')}):</span>
          <span>{isFreeShipping 
            ? t('cart.freeShipping', 'Free') 
            : `${currencySymbol}${shippingCost.toFixed(2)}`
          }</span>
        </SummaryRow>
        <SummaryRow className="total">
          <span>{t('cart.total')}:</span>
          <span>{currencySymbol}{total.toFixed(2)}</span>
        </SummaryRow>

        <ButtonGroup>
          <Button
            variant="primary"
            size="large"
            onClick={handleCheckout}
            disabled={!isFormValid() || isCheckingOut}
            style={{ flex: 1 }}
          >
            {isCheckingOut ? t('cart.processing', 'Processing...') : t('cart.checkout')}
          </Button>
          <Button
            variant="outline"
            size="large"
            onClick={() => navigate('/')}
            style={{ flex: 1 }}
          >
            {t('cart.continueShopping')}
          </Button>
        </ButtonGroup>
      </CartSummary>
    </CartContainer>
  );
};